import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "../../store";
import metaEnv from "../../helper/metaEnv";
import { ESliceAuth } from "../../store/slices/authSlice";
import { ESliceMenu } from "../../store/slices/menuSlice";
import { logout } from "../../utilitie/Auth";
import { setAccessToken } from "../../store/slices/authSlice";
import {
    isAdminSessionExpiredError,
    notifyAdminSessionExpired,
} from "../auth/adminSessionEvents";
import {
    resolveRefreshedAdminSessionTokens,
    shouldAttemptAdminSessionRefresh,
    shouldNotifyAdminSessionExpiredAfterRefreshFailure,
    shouldNotifyAdminSessionExpired,
} from "../auth/adminSessionRecovery";

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
    _retry?: boolean;
}

let refreshTokenPromise: Promise<{ access_token?: string; refresh_token?: string }> | null = null;

const onRequest = (config: AxiosRequestConfig): AxiosRequestConfig => {
    const storeState = store.getState();
    const AuthInfo = storeState[ESliceAuth.AuthInfo];
    const menu = storeState[ESliceMenu.menu];

    //set header
    if (config.headers) {
        const pathURL = config.url || "";
        if (pathURL !== "/auth/login") {
            //set token
            if (AuthInfo.access_token && pathURL !== "/auth/login") config.headers["Authorization"] = `Bearer ${AuthInfo.access_token}`;
            //set menu
            if (menu.code) config.headers["role_code"] = menu.code;
        }
    }
    metaEnv.axiosLogger && console.log("[axios request]", config);
    return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
    metaEnv.axiosLogger && console.log("[axios request error]", error);
    return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
    metaEnv.axiosLogger && console.log("[axios response]", response);
    return response;
};

export const requestNewAccessToken = (refreshToken: string) => {
    if (!refreshTokenPromise) {
        refreshTokenPromise = axios
            .post(
                "/auth/req-access-token",
                {},
                {
                    headers: {
                        "refresh-token": refreshToken
                    }
                }
            )
            .then((res) => res.data)
            .finally(() => {
                refreshTokenPromise = null;
            });
    }

    return refreshTokenPromise;
};

const createResponseErrorHandler = (axiosInstance: AxiosInstance) => async (error: AxiosError): Promise<AxiosResponse> => {
    metaEnv.axiosLogger && console.log("[axios response error]", error);

    const storeState = store.getState();
    const AuthInfo = storeState[ESliceAuth.AuthInfo];
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

    if (error.response?.status === 405) {
        //not allowed (have no permission)
        logout();
        location && location.reload();
    } else if (shouldAttemptAdminSessionRefresh(error) && AuthInfo.refresh_token && originalRequest) {
        originalRequest._retry = true;
        try {
            const { access_token, refresh_token } = await requestNewAccessToken(AuthInfo.refresh_token);
            const refreshedTokens = resolveRefreshedAdminSessionTokens(
                { access_token, refresh_token },
                AuthInfo.refresh_token
            );

            if (refreshedTokens) {
                store.dispatch(setAccessToken(refreshedTokens));
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers["Authorization"] = `Bearer ${refreshedTokens.access_token}`;
                return axiosInstance(originalRequest);
            }
            notifyAdminSessionExpired();
        } catch {
            if (shouldNotifyAdminSessionExpiredAfterRefreshFailure(error)) {
                notifyAdminSessionExpired();
            }
        }
    } else if (
        isAdminSessionExpiredError(error) ||
        shouldNotifyAdminSessionExpired(error, Boolean(AuthInfo.refresh_token))
    ) {
        notifyAdminSessionExpired();
    } else {
    }
    return Promise.reject(error);
};

export function setupInterceptorsTo(axiosInstance: AxiosInstance, baseUrl: string): AxiosInstance {
    axiosInstance.defaults.baseURL = baseUrl;
    axiosInstance.defaults.withCredentials = true;
    axiosInstance.interceptors.request.use(onRequest, onRequestError);
    axiosInstance.interceptors.response.use(onResponse, createResponseErrorHandler(axiosInstance));
    axiosInstance.defaults.timeout = 1000 * 15; //15sec
    return axiosInstance;
}
